import type { DocumentStructureFolderItem, Tables } from "@msuite/utils/types";
import { useImperativeHandle, useMemo } from "react";
import { Checkbox } from "../../checkbox";
import { useFileExplorerContext } from "./context";
import { checkItemsState, handleBatchSelectedFiles } from "./shared";

/** Props Interface */
interface FileExplorerFileCheckboxProps<T extends { file: Tables<"files"> }> {
	item: DocumentStructureFolderItem<T>;
	imperativeRef: React.RefObject<{
		toggleCheckbox: () => void;
	}>;
}

export function FileExplorerFolderCheckbox<T extends { file: Tables<"files"> }>({
	item,
	imperativeRef,
}: FileExplorerFileCheckboxProps<T>) {
	/** Context */
	const { selectedFiles, setSelectedFiles, focusedItem } = useFileExplorerContext();

	const isFocused = focusedItem?.id === item.id;

	/** Memoized */
	const state = useMemo(() => {
		const state = checkItemsState(item.items || [], selectedFiles);
		return state;
	}, [selectedFiles, item, isFocused]);

	/** Functions */
	function handleOnCheckboxToggle() {
		const newSelectedFiles = handleBatchSelectedFiles(state, item.items, selectedFiles);
		setSelectedFiles(newSelectedFiles);
	}

	/** Hooks */
	useImperativeHandle(imperativeRef, () => ({
		toggleCheckbox: handleOnCheckboxToggle,
	}));

	/** Render */
	return (
		<Checkbox
			value={state !== "unchecked"}
			isIndeterminate={state === "indeterminate"}
			onChange={handleOnCheckboxToggle}
		/>
	);
}
